/**
 * Centralized price calculation utilities for tire and wheel pricing
 * This module provides pure functions for consistent price calculations across the application
 */

import { ITireItemDetail } from "features/car_model/types";
import { PunctureProtection } from "features/mst_option/types/option";
import { CostPriceMap } from "features/mst_proc_fee/types/proc-fee";
import { TAX_RATE } from "config";

/**
 * Interface for tire cost settings used in calculations
 */
export interface TireCostSettings {
  replac_labor_cost: number;
  waste_tire_cost: number;
  rubber_stem_valve_cost: number;
  nitrogen_inflation_cost: number;
  hub_rust_prevention_cost: number;
  tire_puncture_flag: number;
  cashback_flag: boolean;
  cashback_period: number;
}

/**
 * Interface for calculation result
 */
export interface PriceCalculationResult {
  basePrice: number;
  discountedPrice: number;
  wage: number;
  totalPrice: number;
  compensation: number;
  totalPriceWithCompensation: number;
  cashbackAmount: number;
  confirmTotalPrice: number;
}

/**
 * Constants
 */
/** 期間中 — same numeric value as `CASHBACK_PERIOD_OPTIONS` in mst_proc_fee constants */
export const CASHBACK_PERIOD_IN_CAMPAIGN = 2;

const DEFAULT_SPECIAL_PRICE = 50000;
const PERCENTAGE_DIVISOR = 100;
const DEFAULT_PROFIT_RATIO = 25;

function toFiniteNumber(value: number | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Case 1: sale_price_rate_atable is null or 0 — list_price × TAX (floor) × quantity
 */
function calculateBasePriceCaseListPrice(tire: ITireItemDetail, quantity: number): number {
  if (quantity === 0) return 0;
  const listPrice = toFiniteNumber(tire.list_price);
  if (listPrice === null) return 0;
  const priceWithTax = Math.floor(listPrice * TAX_RATE);
  return priceWithTax * quantity;
}

/**
 * Case 2: sale_price_rate_atable > 0 — floor(a_price × TAX) then floor(× rate) × quantity
 */
function calculateBasePriceCaseATable(tire: ITireItemDetail, quantity: number): number {
  if (quantity === 0) return 0;
  const aPrice = toFiniteNumber(tire.a_price);
  const rateRaw = tire.sale_price_rate_atable;
  const rate = rateRaw === null || rateRaw === undefined ? NaN : Number(rateRaw);
  if (aPrice === null || !Number.isFinite(rate)) return 0;
  const step1 = Math.floor(aPrice * TAX_RATE);
  const step2 = Math.floor(step1 * rate);
  return step2 * quantity;
}

/** Case 2 applies only when rate is a finite number strictly greater than 0 */
function isSalePriceRateAtablePositive(
  salePriceRateAtable: ITireItemDetail["sale_price_rate_atable"],
): boolean {
  if (salePriceRateAtable === null || salePriceRateAtable === undefined) return false;
  const n = Number(salePriceRateAtable);
  return Number.isFinite(n) && n > 0;
}

/**
 * Calculate base price from tire data
 * @param tire - The tire item detail
 * @param quantity - Number of tires
 * @param costPriceMap - Optional map of item_cd to cost_price from API
 * @param forceStandardPricing - If true, always use standard pricing (list_price/a_price) regardless of price_setting_wheel
 * @returns Base price before discounts
 */
export function calculateBasePrice(
  tire: ITireItemDetail,
  quantity: number,
  costPriceMap?: CostPriceMap,
  forceStandardPricing?: boolean,
): number {
  // Special (cost-based) pricing only when the store-level flag is explicitly 1.
  // Attribute is undefined on tire-single (/mst-proc-fee) flow, so `!== 0` would misfire.
  if (!forceStandardPricing && tire.price_setting_wheel === 1) {
    // Use cost_price from API if available, otherwise fallback to default
    const costPrice = costPriceMap?.[tire.item_cd];
    if (costPrice === undefined) {
      return DEFAULT_SPECIAL_PRICE;
    }

    // Use default profit ratio of 25 if set_profit_ratio is null or undefined
    const profitRatio =
      tire.set_profit_ratio !== null &&
      tire.set_profit_ratio !== undefined &&
      tire.set_profit_ratio > 0
        ? tire.set_profit_ratio
        : DEFAULT_PROFIT_RATIO;

    // Step 1: Calculate base price excluding tax: roundup(costPrice / (1 - profitRatio / 100))
    const profitRatioDecimal = profitRatio / PERCENTAGE_DIVISOR;
    const basePriceExcludingTax = Math.ceil(costPrice / (1 - profitRatioDecimal));

    // Step 2: Calculate base price including tax: rounddown(basePriceExcludingTax × TAX_RATE)
    const basePriceIncludingTax = Math.floor(basePriceExcludingTax * TAX_RATE);

    // Step 3: Multiply by quantity: basePriceIncludingTax * quantity
    return basePriceIncludingTax * quantity;
  }

  // Standard pricing: Case 1 (null / 0 / non-positive) vs Case 2 (rate > 0)
  return isSalePriceRateAtablePositive(tire.sale_price_rate_atable)
    ? calculateBasePriceCaseATable(tire, quantity)
    : calculateBasePriceCaseListPrice(tire, quantity);
}

/**
 * Calculate discounted price with special 4-tire pricing
 * @param basePrice - The base price
 * @param tire - The tire item detail
 * @param quantity - Number of tires
 * @returns Discounted price
 */
export function calculateDiscountedPrice(
  basePrice: number,
  tire: ITireItemDetail,
  quantity: number,
): number {
  // Special 4-tire pricing takes precedence
  if (quantity === 4 && tire.tire_4_price > 0) {
    return tire.tire_4_price;
  }

  // For wheel flow, check winter_wheel_4_price
  if (quantity === 4 && tire.winter_wheel_4_price > 0) {
    return tire.winter_wheel_4_price;
  }

  // Apply discount percentage
  const discount = tire.special_discount ?? tire.fixed_discount;
  return Math.round(basePrice * (1 - discount / PERCENTAGE_DIVISOR));
}

/**
 * Calculate total wage cost from tire cost settings
 * @param settings - Tire cost settings
 * @param quantity - Number of tires
 * @returns Total wage cost
 */
export function calculateWage(settings: TireCostSettings, quantity: number): number {
  return Math.round(
    (settings.replac_labor_cost +
      settings.waste_tire_cost +
      settings.rubber_stem_valve_cost +
      settings.nitrogen_inflation_cost +
      settings.hub_rust_prevention_cost) *
      quantity,
  );
}

/**
 * Calculate puncture protection compensation
 * @param totalPrice - Total price before compensation
 * @param settings - Tire cost settings
 * @param punctureProtection - List of puncture protection options
 * @returns Compensation amount
 */
export function calculateCompensation(
  totalPrice: number,
  settings: TireCostSettings,
  punctureProtection: PunctureProtection[] | null | undefined,
): number {
  // No compensation if disabled or no data
  if (!punctureProtection || settings.tire_puncture_flag === 0) {
    return 0;
  }

  // Find matching protection tier
  const matchedProtection = punctureProtection.find(
    (item) =>
      item.coverage_amount_low <= totalPrice &&
      item.coverage_amount_high >= totalPrice &&
      item.puncture_protection_name.includes(`${settings.tire_puncture_flag}本`),
  );

  return matchedProtection?.coverage_enrollment_fee || 0;
}

/** True when cashback period is 期間中 (stored as `2`; `1` = 期間外). */
export function isCashbackCampaignPeriodActive(cashbackPeriod: number | string): boolean {
  const n = Number(cashbackPeriod);
  return Number.isFinite(n) && n === CASHBACK_PERIOD_IN_CAMPAIGN;
}

/** Checkbox ON かつ 期間中 — 見積控除とホイール見積作成 API の実額送信で共通 */
export function shouldApplyCashbackToWheelQuote(settings: {
  cashback_flag: boolean;
  cashback_period: number | string;
}): boolean {
  return Boolean(
    settings.cashback_flag && isCashbackCampaignPeriodActive(settings.cashback_period),
  );
}

/**
 * Calculate cashback amount subtracted from 見積金額.
 * Applies only when the cashback checkbox is on and the period is 期間中.
 */
export function calculateCashback(settings: TireCostSettings, cbTirePrice: number): number {
  return shouldApplyCashbackToWheelQuote(settings) ? cbTirePrice : 0;
}

/**
 * Main calculation function that combines all price calculations
 * @param tire - The tire item detail
 * @param quantity - Number of tires
 * @param settings - Tire cost settings
 * @param punctureProtection - List of puncture protection options
 * @param costPriceMap - Optional map of item_cd to cost_price from API (for special pricing)
 * @param forceStandardPricing - If true, always use standard pricing (list_price/a_price) regardless of price_setting_wheel
 * @returns Complete price calculation result
 */
export function calculateTirePrice(
  tire: ITireItemDetail,
  quantity: number,
  settings: TireCostSettings,
  punctureProtection?: PunctureProtection[] | null,
  costPriceMap?: CostPriceMap,
  forceStandardPricing?: boolean,
): PriceCalculationResult {
  // Step 1: Calculate base price
  const basePrice = calculateBasePrice(tire, quantity, costPriceMap, forceStandardPricing);

  // Step 2: Apply discounts
  const discountedPrice = calculateDiscountedPrice(basePrice, tire, quantity);

  // Step 3: Calculate wage costs
  const wage = calculateWage(settings, quantity);

  // Step 4: Calculate total before compensation
  const totalPrice = Math.round(discountedPrice + wage);

  // Step 5: Calculate puncture protection compensation
  const compensation = calculateCompensation(totalPrice, settings, punctureProtection);

  // Step 6: Add compensation
  const totalPriceWithCompensation = totalPrice + compensation;

  // Step 7: Calculate cashback
  const cashbackAmount = calculateCashback(settings, tire.cb_tire_price || 0);

  // Step 8: Final price after cashback
  const confirmTotalPrice = totalPriceWithCompensation - cashbackAmount;

  return {
    basePrice,
    discountedPrice,
    wage,
    totalPrice,
    compensation,
    totalPriceWithCompensation,
    cashbackAmount,
    confirmTotalPrice,
  };
}

/**
 * Helper to convert cost setting info to TireCostSettings
 * @param costSetting - Cost setting from state (with string values)
 * @param parseNumber - Function to parse number from formatted string
 * @returns TireCostSettings object
 */
export function convertToTireCostSettings(
  costSetting: {
    replac_labor_cost: string | number;
    waste_tire_cost_flag: boolean;
    waste_tire_cost: string | number;
    rubber_stem_valve_flag: boolean;
    rubber_stem_valve_cost: string | number;
    nitrogen_inflation_flag: boolean;
    nitrogen_inflation_cost: string | number;
    hub_rust_prevention_flag: boolean;
    hub_rust_prevention_cost: string | number;
    tire_puncture_flag: number;
    cashback_flag: boolean;
    cashback_period: string | number;
  },
  parseNumber: (value: string | number) => number,
): TireCostSettings {
  return {
    replac_labor_cost: parseNumber(costSetting.replac_labor_cost),
    waste_tire_cost: costSetting.waste_tire_cost_flag
      ? parseNumber(costSetting.waste_tire_cost)
      : 0,
    rubber_stem_valve_cost: costSetting.rubber_stem_valve_flag
      ? parseNumber(costSetting.rubber_stem_valve_cost)
      : 0,
    nitrogen_inflation_cost: costSetting.nitrogen_inflation_flag
      ? parseNumber(costSetting.nitrogen_inflation_cost)
      : 0,
    hub_rust_prevention_cost: costSetting.hub_rust_prevention_flag
      ? parseNumber(costSetting.hub_rust_prevention_cost)
      : 0,
    tire_puncture_flag: costSetting.tire_puncture_flag,
    cashback_flag: costSetting.cashback_flag,
    cashback_period:
      typeof costSetting.cashback_period === "number"
        ? costSetting.cashback_period
        : Number.isNaN(Number(costSetting.cashback_period))
          ? 1
          : Number(costSetting.cashback_period),
  };
}
